import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { X } from 'lucide-react'
import clsx from 'clsx'

export type BannerVariant = 'info' | 'warning' | 'danger' | 'success'

export interface BannerProps {
  variant: BannerVariant
  icon: LucideIcon
  title: string
  children?: ReactNode
  action?: {
    label: string
    onClick: () => void
  }
  /** Renders a close (X) button that calls this instead of `action` - PRD
   * 05's "shown once" info banner, dismissed state persisted by the caller
   * (e.g. `useDismissedBanner`), not this component. */
  onDismiss?: () => void
}

const VARIANT_CLASSES: Record<BannerVariant, string> = {
  info: 'border-accent/40 bg-accent/10 text-accent',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  danger: 'border-danger/40 bg-danger/10 text-danger',
  success: 'border-success/40 bg-success/10 text-success',
}

// Inline banner (draft-pending / danger states) - shared across PRDs 01/05/07,
// none of which owned a home for this pattern in PRD 00's component set.
export function Banner({ variant, icon: Icon, title, children, action, onDismiss }: BannerProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-4 rounded-control border px-4 py-3',
        VARIANT_CLASSES[variant],
      )}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} strokeWidth={1.5} />
        <div className="flex flex-col gap-0.5">
          <span className="text-body font-medium text-text-primary">{title}</span>
          {children && <span className="text-meta text-text-secondary">{children}</span>}
        </div>
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="shrink-0 rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
        >
          {action.label}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0 text-text-secondary hover:text-text-primary"
        >
          <X size={16} strokeWidth={1.5} />
        </button>
      )}
    </div>
  )
}
